/**
 * Pricing rule shared with the admin app:
 * the unit price covers up to `baseGuests` people; each guest beyond that
 * adds `unitPrice × extraGuestRatio%`.
 */
export interface PricingInput {
  unitPrice: number;
  guestsCount: number;
  baseGuests?: number | null;
  extraGuestRatio?: number | null;
  /** Coupon discount to subtract from the subtotal (applied last) */
  couponDiscount?: number;
}

export interface PricingBreakdown {
  /** Subtotal before coupon (base price + surcharge) */
  subtotal: number;
  /** Final amount — subtotal minus coupon discount */
  total: number;
  /** Coupon discount applied (0 when none) */
  discountAmount: number;
  /** Base price (covers `baseGuests`) */
  basePrice: number;
  /** Guests above `baseGuests` */
  extraGuests: number;
  /** Surcharge for all extra guests combined */
  extraCharge: number;
  extraGuestRatio: number;
}

export function computeTotal(input: PricingInput): PricingBreakdown {
  const { unitPrice, guestsCount } = input;
  const baseGuests = Math.max(1, input.baseGuests || 1);
  const extraGuestRatio = input.extraGuestRatio ?? 0;
  const extraGuests = Math.max(0, guestsCount - baseGuests);
  const extraCharge = Math.round(
    unitPrice * (extraGuestRatio / 100) * extraGuests,
  );
  const subtotal = unitPrice + extraCharge;
  const discountAmount = Math.max(0, Math.min(input.couponDiscount ?? 0, subtotal));
  return {
    subtotal,
    total: subtotal - discountAmount,
    discountAmount,
    basePrice: unitPrice,
    extraGuests,
    extraCharge,
    extraGuestRatio,
  };
}
